
import {calcRegion} from 'utils/calcRegion';
import {getLocalId} from 'utils/localId';
import {Endpoints, postValues} from 'utils/n8n';
import {pickOrderRegion} from 'utils/orderRegion';

import {RegionAction, RegionState, SOURCE_PRIORITY} from './types';

type Coords = {lat: number; lng: number};

// pick settles the metro's best ordering region for a position
// (IP lookups that fail report 0,0, which isn't one)
const pick = (state: RegionState, coords?: Coords): RegionState => ({
	...state,
	orderRegion: pickOrderRegion(
		state.region,
		coords && (coords.lat || coords.lng) ? coords : undefined,
	),
	orderSettled: true,
});

// settle decides the ordering region for the rest of the page view once
// it can't change: the metro has at most one, the visitor's location
// picks among several, or geolocation found no metro (national
// products). force settles with what's known so far (a timeout).
const settle = (state: RegionState, force = false): RegionState => {
	if (state.orderSettled || !state.ready) {
		return state;
	}

	const located = state.geoCoords !== undefined;
	const known = force || (
		state.region
			? state.region.orderRegions.length <= 1 || located
			: located && !calcRegion(state.geoCoords)
	);

	return known ? pick(state, state.geoCoords) : state;
};

export const regionReducer = (
	state: RegionState,
	action: RegionAction,
): RegionState => {
	switch (action.type) {
	case 'SET_REGION': {
		// Only update if new source has >= priority than current
		if (state.source && SOURCE_PRIORITY[action.source] < SOURCE_PRIORITY[state.source]) {
			return state;
		}

		const next = {
			...state,
			region: action.region,
			coords: action.coords ?? action.region.center,
			label: action.region.name,
			source: action.source,
		};

		// Picking a metro picks its best ordering region for where the
		// visitor is
		return action.source === 'MANUAL' ? pick(next, state.geoCoords) : settle(next);
	}

	case 'SET_COORDS': {
		const region = calcRegion(action.coords);

		// If we already have a region, only update
		// if these coordinates are within that region
		if (action.skipIf === 'mismatched') {
			if (state.region && region &&
				state.region.key !== region?.key) {
				return state;
			}
		}

		// Save information for analysis
		if (action.label && action.coords) {
			postValues(Endpoints.userGPS, {
				uniq: getLocalId(),
				name: action.label,
				region: region?.key || '',
				coords: action.coords,
			});
		}

		const next = {
			...state,
			coords: action.coords,
			region,
			label: action.label,
			source: action.source,
		};

		// Searching an address picks the best ordering region for it
		return action.source === 'MANUAL' ? pick(next, action.coords) : settle(next);
	}

	case 'GEO_COMPLETE':
		return settle({
			...state,
			geoCoords: action.coords,
		});

	case 'SET_ORDER_REGION':
		return {
			...state,
			orderRegion: action.orderRegion,
			orderSettled: true,
		};

	case 'SETTLE_ORDER_REGION':
		return settle(state, true);

	case 'READY':
		return settle({
			...state,
			ready: true,
		});

	// Clearing a location search leaves prices as they are
	case 'CLEAR_REGION':
		return {
			geoCoords: state.geoCoords,
			ready: state.ready,
			orderRegion: state.orderRegion,
			orderSettled: state.orderSettled,
		};
	}
};
